import { Box, Divider, Typography } from "@mui/material";
import type { ReactNode } from "react";
import { SectionCard } from "@/components/DesignSystem";
import { colors, eventTypeColors } from "@/theme/eventsTokens.ts";
import { formatShare } from "@/utils/formatShare.ts";
import type { CredentialFunnel } from "@/types";
import { attackTotals, noAttackTrafficMessage } from "@/utils/attackTraffic.ts";

interface Row {
  readonly label: string;
  readonly count: number;
  readonly denom: number;
  readonly color: string;
  readonly hint?: string;
  readonly emphasis?: boolean;
  readonly muted?: boolean;
  readonly indent?: boolean;
}

export default function CredentialDefenseFunnel({
  funnel,
  windowDays,
}: {
  readonly funnel: CredentialFunnel;
  readonly windowDays: number;
}) {
  const { attackTotal, reachedCreds } = attackTotals(funnel);
  return (
    <SectionCard title="How sign-in attempts are filtered">
      {attackTotal === 0 ? (
        <Empty>{noAttackTrafficMessage(windowDays)}</Empty>
      ) : (
        <>
          <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: "block" }}>
            Last {windowDays} days of attack traffic from hostile sources — addresses with no
            successful sign-in in the window — by the stage each attempt reached.
          </Typography>
          <ShieldBand blocked={funnel.blocked} reachedCreds={reachedCreds} attackTotal={attackTotal} />
          <CredentialBand funnel={funnel} reachedCreds={reachedCreds} />
        </>
      )}
      <ExcludedNote funnel={funnel} />
    </SectionCard>
  );
}

function ShieldBand({
  blocked,
  reachedCreds,
  attackTotal,
}: {
  readonly blocked: number;
  readonly reachedCreds: number;
  readonly attackTotal: number;
}) {
  return (
    <Box sx={{ mb: 2.5 }}>
      <BandLabel>Shield filtration</BandLabel>
      <FunnelRow
        row={{
          label: "Blocked early by Shield",
          count: blocked,
          denom: attackTotal,
          color: colors.textSecondary,
          hint: "Filtered by Shield before any credentials were tested.",
        }}
      />
      <FunnelRow
        row={{
          label: "Reached credential check",
          count: reachedCreds,
          denom: attackTotal,
          color: colors.primary,
          hint: "Passed Shield and proceeded to credential verification.",
        }}
      />
    </Box>
  );
}

function CredentialBand({
  funnel,
  reachedCreds,
}: {
  readonly funnel: CredentialFunnel;
  readonly reachedCreds: number;
}) {
  if (reachedCreds === 0) {
    return (
      <Box>
        <Divider sx={{ mb: 2 }} />
        <Typography variant="body2" color="text.secondary">
          Every attack attempt was blocked by Shield before the credential check.
        </Typography>
      </Box>
    );
  }
  // Only hostile sources are counted, so everything reaching MFA here is a failed
  // second factor from a source that never signs in successfully — the same
  // population the SecondFactorCard headlines.
  const rows: Row[] = [
    { label: "User not found", count: funnel.user_not_found, denom: reachedCreds, color: eventTypeColors.user_not_found, hint: "Username does not exist — enumeration or password spray." },
    { label: "Invalid password", count: funnel.invalid_password, denom: reachedCreds, color: eventTypeColors.invalid_password, hint: "Valid username, wrong password." },
    { label: "Reached MFA", count: funnel.failed_second_factor, denom: reachedCreds, color: eventTypeColors.failed_second_factor, hint: "Correct password but stopped by the second factor, from a source that never signs in successfully — treat these credentials as compromised. Sources in the card below." },
  ];
  return (
    <Box>
      <Divider sx={{ mb: 2 }} />
      <BandLabel>Of the {reachedCreds.toLocaleString()} that reached the credential check</BandLabel>
      {rows.map((row) => (
        <FunnelRow key={row.label} row={row} />
      ))}
    </Box>
  );
}

// The trusted sources' traffic is left out of every stage; say what was left out
// rather than let the customer wonder where their own users' typos went.
function ExcludedNote({ funnel }: { readonly funnel: CredentialFunnel }) {
  const { successful, excluded_trusted_attempts: excluded } = funnel;
  if (successful === 0 && excluded === 0) return null;
  return (
    <>
      <Divider sx={{ mt: 2, mb: 1.5 }} />
      <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
        Excluded: {successful.toLocaleString()} sign-ins and {excluded.toLocaleString()} failed or blocked
        attempts from sources that also sign in normally — typos and MFA problems, not attacks.
      </Typography>
    </>
  );
}

function FunnelRow({ row }: { readonly row: Row }) {
  const pct = row.denom > 0 ? (row.count / row.denom) * 100 : 0;
  return (
    <Box sx={{ mb: 1.5, pl: row.indent ? 3 : 0, opacity: row.muted ? 0.85 : 1 }}>
      <RowHeader row={row} />
      <RowBar row={row} pct={pct} />
      {row.hint && (
        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.25, display: "block" }}>
          {row.hint}
        </Typography>
      )}
    </Box>
  );
}

function rowLabelColor(row: Row): string {
  if (row.emphasis) return row.color;
  if (row.muted) return colors.textMuted;
  return colors.textPrimary;
}

function RowHeader({ row }: { readonly row: Row }) {
  const labelColor = rowLabelColor(row);
  return (
    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", mb: 0.5, gap: 1 }}>
      <Typography variant="body2" sx={{ fontWeight: row.emphasis ? 700 : 500, color: labelColor }}>
        {row.label}
      </Typography>
      <Box sx={{ display: "flex", gap: 1.5, alignItems: "baseline", flexShrink: 0 }}>
        <Typography variant="body2" sx={{ fontWeight: 700, minWidth: 64, textAlign: "right" }}>
          {row.count.toLocaleString()}
        </Typography>
        <Typography variant="caption" sx={{ color: "text.secondary", minWidth: 52, textAlign: "right" }}>
          {formatShare(row.count, row.denom)}
        </Typography>
      </Box>
    </Box>
  );
}

function RowBar({ row, pct }: { readonly row: Row; readonly pct: number }) {
  const barWidth = row.count > 0 ? Math.max(pct, 1.5) : 0;
  return (
    <Box sx={{ height: row.emphasis ? 10 : 8, borderRadius: 4, bgcolor: colors.backgroundMuted, overflow: "hidden" }}>
      <Box
        sx={{ width: `${barWidth}%`, height: "100%", borderRadius: 4, bgcolor: row.color, transition: "width 0.3s ease" }}
      />
    </Box>
  );
}

function BandLabel({ children }: { readonly children: ReactNode }) {
  return (
    <Typography
      variant="overline"
      sx={{ display: "block", color: colors.textMuted, fontWeight: 600, letterSpacing: "0.08em", mb: 1 }}
    >
      {children}
    </Typography>
  );
}

function Empty({ children }: { readonly children: ReactNode }) {
  return (
    <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
      {children}
    </Typography>
  );
}
